import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useEvent } from '@/hooks/useEvent'
import { supabase } from '@/lib/supabaseClient'
import type { CompanyRow, PortalStatus } from '@/lib/types'
import { Badge, Button, Card, EmptyState, Field, Input, Modal, PageSpinner, PortalStatusBadge, Textarea, useToast } from '@/components/ui'
import { formatDateTime, formatRelative } from '@/lib/format'
import { companyPortalUrl } from '@/lib/env'

interface Summary {
  company_id: string
  rooms: number
  guest_capacity: number
  guests_entered: number
  remaining_slots: number
  portal_status: PortalStatus
  deadline_at: string | null
  submission_status: string
  last_activity_at: string | null
}

export default function Companies() {
  const { event } = useEvent()
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<CompanyRow[] | null>(null)
  const [summaries, setSummaries] = useState<Record<string, Summary>>({})
  const [showCreate, setShowCreate] = useState(false)
  const [tokens, setTokens] = useState<Record<string, string>>({})
  const { push } = useToast()

  async function load() {
    const [{ data: companyRows, error: e1 }, { data: summaryRows, error: e2 }, { data: portalRows }] = await Promise.all([
      supabase.from('companies').select('*').eq('event_id', event.id).is('archived_at', null).order('name'),
      supabase.rpc('admin_company_summaries', { p_event_id: event.id }),
      supabase.from('company_portals').select('company_id, portal_token').eq('event_id', event.id),
    ])
    if (e1) push(e1.message, 'error')
    if (e2) push(e2.message, 'error')
    setCompanies((companyRows as CompanyRow[]) || [])
    const map: Record<string, Summary> = {}
    for (const s of (summaryRows as Summary[]) || []) map[s.company_id] = s
    setSummaries(map)
    const tmap: Record<string, string> = {}
    for (const p of (portalRows as { company_id: string; portal_token: string }[]) || []) tmap[p.company_id] = p.portal_token
    setTokens(tmap)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id])

  async function copyLink(companyId: string) {
    const token = tokens[companyId]
    if (!token) return
    await navigator.clipboard.writeText(companyPortalUrl(token))
    push('Link copied')
  }

  if (companies === null) return <PageSpinner label="Loading companies…" />

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-slate-900">Companies</h1>
          <p className="text-sm text-slate-500">Each company only sees rooms allocated to it.</p>
        </div>
        <Button variant="primary" onClick={() => setShowCreate(true)}>
          + Add Company
        </Button>
      </div>

      {companies.length === 0 ? (
        <EmptyState
          title="No companies have been added yet"
          action={
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              Add Company
            </Button>
          }
        />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2.5">Company</th>
                <th className="px-4 py-2.5">Contact</th>
                <th className="px-4 py-2.5 text-right">Rooms</th>
                <th className="px-4 py-2.5 text-right">Capacity</th>
                <th className="px-4 py-2.5 text-right">Guests</th>
                <th className="px-4 py-2.5 text-right">Remaining</th>
                <th className="px-4 py-2.5">Deadline</th>
                <th className="px-4 py-2.5">Status</th>
                <th className="px-4 py-2.5">Last Update</th>
                <th className="px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {companies.map((c) => {
                const s = summaries[c.id]
                return (
                  <tr key={c.id} className="hover:bg-slate-50">
                    <td className="cursor-pointer px-4 py-2.5 font-medium text-slate-900" onClick={() => navigate(`/admin/events/${event.id}/companies/${c.id}`)}>
                      {c.name}
                      <div className="text-xs font-normal text-slate-400">{c.code}</div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{c.contact_person || '-'}</td>
                    <td className="px-4 py-2.5 text-right">{s?.rooms ?? 0}</td>
                    <td className="px-4 py-2.5 text-right">{s?.guest_capacity ?? 0}</td>
                    <td className="px-4 py-2.5 text-right">{s?.guests_entered ?? 0}</td>
                    <td className="px-4 py-2.5 text-right">{s?.remaining_slots ?? 0}</td>
                    <td className="px-4 py-2.5 text-slate-600">{s?.deadline_at ? formatDateTime(s.deadline_at) : '-'}</td>
                    <td className="px-4 py-2.5">{s?.portal_status ? <PortalStatusBadge status={s.portal_status} /> : <Badge>-</Badge>}</td>
                    <td className="px-4 py-2.5 text-slate-500">{formatRelative(s?.last_activity_at || null)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="secondary" onClick={() => navigate(`/admin/events/${event.id}/companies/${c.id}`)}>
                          Open
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => copyLink(c.id)}>
                          Copy Link
                        </Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      <CreateCompanyModal
        open={showCreate}
        eventId={event.id}
        onClose={() => setShowCreate(false)}
        onCreated={(id) => {
          setShowCreate(false)
          navigate(`/admin/events/${event.id}/companies/${id}`)
        }}
      />
    </div>
  )
}

function CreateCompanyModal({ open, eventId, onClose, onCreated }: { open: boolean; eventId: string; onClose: () => void; onCreated: (id: string) => void }) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [contactPerson, setContactPerson] = useState('')
  const [contactMobile, setContactMobile] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const { push } = useToast()

  async function submit() {
    if (!name.trim() || !code.trim()) {
      push('Company name and code are required', 'error')
      return
    }
    setSaving(true)
    const { data, error } = await supabase
      .from('companies')
      .insert({
        event_id: eventId,
        name: name.trim(),
        code: code.trim().toUpperCase(),
        contact_person: contactPerson.trim() || null,
        contact_mobile: contactMobile.trim() || null,
        contact_email: contactEmail.trim() || null,
        notes: notes.trim() || null,
      })
      .select('id')
      .single()
    setSaving(false)
    if (error) {
      push(error.message.includes('duplicate') ? 'That company code is already used in this event' : error.message, 'error')
      return
    }
    push('Company created — a secure portal link was generated automatically')
    onCreated(data!.id as string)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Company"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Create Company
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Company Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="EVA Pharma" />
        </Field>
        <Field label="Company Code" required hint="Unique within this event.">
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="EVA" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contact Person">
            <Input value={contactPerson} onChange={(e) => setContactPerson(e.target.value)} />
          </Field>
          <Field label="Contact Mobile">
            <Input value={contactMobile} onChange={(e) => setContactMobile(e.target.value)} />
          </Field>
        </div>
        <Field label="Contact Email">
          <Input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
        </Field>
        <Field label="Notes">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
